import type { FC } from 'react';
import type { ITracker, TrackerConfig } from './types';
import type { TAnalyticsItem } from '../types';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

// Convert TAnalyticsItem to GA4 item format
const toGA4Item = (item: TAnalyticsItem) => ({
  item_id: item.item_id,
  item_name: item.item_name,
  item_variant: item.item_variant,
  item_category: item.item_category,
  item_brand: item.item_brand,
  price: item.price,
  quantity: item.quantity,
  index: item.index,
});

// Get currency from item or fall back to channel default
const getCurrency = (item: TAnalyticsItem, fallback: string) => item.currency || fallback;
const getCurrencyFromItems = (items: TAnalyticsItem[], fallback: string) =>
  items[0]?.currency || fallback;

export const createGATracker = (measurementId: string, config: TrackerConfig): ITracker => {
  const { channel, debug } = config;
  const defaultCurrency = channel.defaultCurrency;

  // Push directly to window.dataLayer — the real event queue.
  // Events are pushed as plain {event, ...params} objects (the standard dataLayer
  // format) so they can be filtered directly in tests and by GTM tag triggers.
  // Events pushed here before the GA script loads are replayed when it arrives.
  const gtag = (command: string, nameOrDate?: unknown, params?: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    if (command === 'event' && typeof nameOrDate === 'string') {
      window.dataLayer.push({ event: nameOrDate, ...params });
    } else {
      window.dataLayer.push([command, nameOrDate, params].filter((x) => x !== undefined));
    }
    if (debug) {
      console.log('[GA4]', command, nameOrDate, params);
    }
  };

  return {
    init() {
      if (typeof window === 'undefined') return;

      // Set up the window.gtag shim for any code that calls it directly,
      // then send the config hit so GA knows to activate this measurement ID.
      window.dataLayer = window.dataLayer || [];
      if (!window.gtag) {
        window.gtag = function gtag() {
          window.dataLayer.push(arguments);
        };
      }
      // Use window.gtag (Arguments-object shim) for init commands — GA4's
      // gtag/js only processes Arguments objects, not plain arrays, so calling
      // the local gtag closure (which pushes arrays) would silently drop the
      // config hit and leave the measurement ID unregistered.
      window.gtag('js', new Date());
      window.gtag('config', measurementId);

      if (debug) {
        console.log('[GA4] Initialized with', measurementId);
      }
    },

    trackPageView({ url, title }) {
      gtag('event', 'page_view', { page_path: url, page_title: title });
    },

    identify({ email, data }) {
      gtag('set', 'user_properties', { email, ...data });
    },

    trackViewItem({ item }) {
      gtag('event', 'view_item', {
        currency: getCurrency(item, defaultCurrency),
        value: item.price,
        items: [toGA4Item(item)],
      });
    },

    trackViewItemList({ items, listId, listName }) {
      gtag('event', 'view_item_list', {
        currency: getCurrencyFromItems(items, defaultCurrency),
        item_list_id: listId,
        item_list_name: listName,
        items: items.map(toGA4Item),
      });
    },

    trackAddToCart({ item, quantity, value }) {
      gtag('event', 'add_to_cart', {
        currency: getCurrency(item, defaultCurrency),
        value,
        items: [{ ...toGA4Item(item), quantity }],
      });
    },

    trackRemoveFromCart({ item, quantity, value }) {
      gtag('event', 'remove_from_cart', {
        currency: getCurrency(item, defaultCurrency),
        value,
        items: [{ ...toGA4Item(item), quantity }],
      });
    },

    trackViewCart({ items, value }) {
      gtag('event', 'view_cart', {
        currency: getCurrencyFromItems(items, defaultCurrency),
        value,
        items: items.map(toGA4Item),
      });
    },

    trackBeginCheckout({ items, value }) {
      gtag('event', 'begin_checkout', {
        currency: getCurrencyFromItems(items, defaultCurrency),
        value,
        items: items.map(toGA4Item),
      });
    },

    trackAddShippingInfo({ items, value, shippingTier }) {
      gtag('event', 'add_shipping_info', {
        currency: getCurrencyFromItems(items, defaultCurrency),
        value,
        shipping_tier: shippingTier,
        items: items.map(toGA4Item),
      });
    },

    trackAddPaymentInfo({ items, value, paymentType }) {
      gtag('event', 'add_payment_info', {
        currency: getCurrencyFromItems(items, defaultCurrency),
        value,
        payment_type: paymentType,
        items: items.map(toGA4Item),
      });
    },

    trackPurchase({ transactionId, items, value, tax, shipping }) {
      gtag('event', 'purchase', {
        transaction_id: transactionId,
        currency: getCurrencyFromItems(items, defaultCurrency),
        value,
        tax,
        shipping,
        items: items.map(toGA4Item),
      });
    },

    trackAddToWaitlist({ email, item, value }) {
      gtag('event', 'add_to_waitlist', {
        currency: getCurrency(item, defaultCurrency),
        value,
        email,
        items: [toGA4Item(item)],
      });
    },

    trackEvent({ event, data }) {
      gtag('event', event, data);
    },
  };
};

interface GAScriptProps {
  measurementId: string;
}

export const GAScript: FC<GAScriptProps> = ({ measurementId }) => {
  return (
    <script
      async
      src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
    />
  );
};
